import { useMutation, useQuery } from '@apollo/client'
import { object, string, bool } from 'yup'
import { useFormik } from 'formik'
import { FC, useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import { Link, Typography, Grid, GlowHeading, Heading } from 'components/ui'
import {
  RegistrationMutation,
  RegistrationMutationVariables,
  UserReferralQuery,
  UserReferralQueryVariables,
} from 'gql'
import { useQueryParams, useDataLayer } from 'hooks'
import { COUNTRIES } from 'constants/addresses'
import { FIND_USER_REFERRAL } from 'gql/queries'
import { EMAIL } from 'constants/schema'
import ReferralSignInModal from 'components/modals/referral-sign-in'
import { GRANTED_REWARDS } from 'routes/paths'
import { useVariables } from 'hooks/useVariables'
import { USER_BASE_DATA } from 'gql/user/queries'
import { REGISTRATION } from 'gql/user/mutations'
import { PARTNER_SLUG } from 'constants/constant'
import SignUpFormComponent from './component'

const validationSchema = object().shape({
  newsletter: bool(),
  lastName: string().required('Last name is required!'),
  firstName: string().required('First name is required!'),
  terms: bool().oneOf([true], 'You must accept the Terms and Conditions'),
  email: EMAIL(),
  password: string()
    .min(6, 'Password must be longer than 6 characters!')
    .required('Password is required!'),
})

const initialValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  country: COUNTRIES[0].value,
  terms: true,
  newsletter: false,
}

type SignUpFormProps = {
  onCompleted?: () => void
  isReferralMode?: boolean
}

const SignUpForm: FC<SignUpFormProps> = ({ onCompleted, isReferralMode }) => {
  const {
    paths,
    query: { redirect, auction, bid, r },
    withQueryParams,
  } = useQueryParams()

  const { push } = useRouter()

  const dl = useDataLayer()
  const { CREDIT_ACTION_TYPES } = useVariables()

  const [isSignInModalVisible, setIsSignInModalVisible] = useState(false)

  const [mutate, { loading }] = useMutation<RegistrationMutation, RegistrationMutationVariables>(
    REGISTRATION,
    { refetchQueries: [USER_BASE_DATA] }
  )

  const { data, loading: findRefLoading } = useQuery<UserReferralQuery, UserReferralQueryVariables>(
    FIND_USER_REFERRAL,
    {
      skip: !r,
      variables: {
        code: r,
      },
    }
  )

  const findReferralError =
    r && !data?.findUserReferral && !findRefLoading
      ? `Referral code "${r}" does not exist. Ensure it is valid to receive an account credit.`
      : ''

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    values,
    errors,
    status,
    touched,
    setStatus,
    setFieldError,
  } = useFormik({
    validationSchema,
    initialValues,
    onSubmit: () =>
      mutate({
        variables: { ...values, referralCode: r, partnerSlug: PARTNER_SLUG },
        onCompleted: ({ signup }) => {
          if (signup.__typename === 'SignupFailure') {
            setStatus(
              signup.errorMessage || 'An error occurred while signing up. Please try again.'
            )

            signup.fieldsErrors.forEach(({ field, messages }) => {
              if (messages.length) {
                setFieldError(field, messages[0])
              }
            })

            return
          }

          dl.push({
            event: 'sign_up',
            method: 'email-pass',
          })

          onCompleted?.()
        },
      }),
  })

  useEffect(() => {
    if (errors.email === 'E-Mail Address Has Already Been Registered.' && (r || isReferralMode)) {
      setIsSignInModalVisible(true)
    }
  }, [errors.email, r])

  return (
    <>
      <div style={{ maxWidth: 500, margin: '0 auto', width: '100%' }}>
        <Grid gap="1.5rem">
          <Grid gap="1rem">
            {r || isReferralMode ? (
              <>
                <Heading colorGlow="g-green" Tag="span" size="h4" uppercase center>
                  You passed the vibe check. Welcome to Granted Rewards!
                </Heading>
                <Typography.Text center>
                  We keep it simple. Take 30 seconds to create an account and receive $
                  {CREDIT_ACTION_TYPES.sign_up} in credit, useable for any future purchase on
                  Granted.
                </Typography.Text>
                {findReferralError && (
                  <Typography.Text color="yellow" center>
                    {findReferralError}
                  </Typography.Text>
                )}
              </>
            ) : (
              <>
                <GlowHeading Tag="h2" size="h1" center>
                  Welcome
                </GlowHeading>

                <Typography.Text center>
                  Create your account or{' '}
                  <Link
                    color="green"
                    href={withQueryParams(paths.LOGIN, { redirect, auction, bid })}
                  >
                    Log In
                  </Link>
                </Typography.Text>
              </>
            )}
          </Grid>

          <SignUpFormComponent
            status={status}
            values={values}
            errors={errors}
            touched={touched}
            loading={loading}
            handleBlur={handleBlur}
            handleSubmit={handleSubmit}
            handleChange={handleChange}
          />
        </Grid>
      </div>

      <ReferralSignInModal
        onClose={() => setIsSignInModalVisible(false)}
        initialValues={{ email: values.email }}
        onCompleted={() => push(GRANTED_REWARDS)}
        isOpen={isSignInModalVisible}
      />
    </>
  )
}

export default SignUpForm
